import {
  darkModeOf,
  drawnFinishValue,
  finishSpec,
  layoutSpec,
  paletteSpec,
  type StudioChoice,
  type StyleModel,
} from '@base/shared';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Panel } from '../components/ui.tsx';
import { FactList } from '../components/patterns/index.ts';
import { localized } from '../i18n/index.ts';
import { CheckList } from './CheckList.tsx';
import { designGuideOf } from './guide.ts';
import { ModelsPanel } from './Models.tsx';
import { optionKey, resolvedStudio, reviewOf } from './state.ts';

/**
 * Etapa 4 (ADR 0133, parte 5): o resumo, a conferência com o conserto, o trecho do guia de design
 * e Meus modelos. A comparação lado a lado e "Ver o sistema" ficam na prévia da bancada.
 */
export function ReviewStep({
  value,
  appName,
  onChange,
  readOnly,
  models,
  onModelsChange,
  onCompare,
}: {
  value: StudioChoice;
  appName: string;
  onChange: (next: StudioChoice) => void;
  readOnly: boolean;
  models: StyleModel[];
  onModelsChange?: (next: StyleModel[]) => Promise<unknown>;
  onCompare: (m: StyleModel) => void;
}) {
  const { t } = useTranslation();
  const review = reviewOf(value, appName);
  const { layout, finish } = resolvedStudio(value);
  const paletteName =
    value.palette.id === 'logo'
      ? t('style.fromLogo')
      : localized(paletteSpec(value.palette.id)?.name);
  const changes = [
    ...review.changedLayout.map(
      (k) =>
        `${t(`style.layoutOptions.${k}.label`)}: ${t(`style.layoutOptions.${k}.values.${layout[k]}`)}`,
    ),
    ...review.changedFinish.map(
      (k) =>
        `${t(`style.finishOptions.${k}.label`)}: ${t(`style.finishOptions.${k}.values.${optionKey(drawnFinishValue(k, finish[k]))}`)}`,
    ),
  ];
  return (
    <>
      <Panel title={t('style.summary')}>
        <FactList
          items={[
            {
              label: t('style.summaryLayout'),
              value: localized(layoutSpec(value.layout.id)?.name),
            },
            { label: t('style.summaryPalette'), value: paletteName },
            {
              label: t('style.summaryFinish'),
              value: localized(finishSpec(value.finish.id)?.name),
            },
            {
              label: t('style.summaryMobile'),
              value: t(`style.mobileNavs.${value.mobileNav ?? 'drawer'}`),
            },
            {
              label: t('style.summaryDark'),
              value: t(`style.darkMode.values.${darkModeOf(value)}`),
            },
            {
              label: t('style.summaryChanges'),
              value: changes.length ? changes.join(' · ') : t('style.noChanges'),
            },
          ]}
        />
      </Panel>
      <Panel title={t('style.check')}>
        <CheckList value={value} onChange={onChange} readOnly={readOnly} />
      </Panel>
      <GuidePanel value={value} appName={appName} />
      <ModelsPanel
        value={value}
        onChange={onChange}
        models={models}
        onModelsChange={onModelsChange}
        onCompare={onCompare}
        readOnly={readOnly}
      />
    </>
  );
}

/** O trecho do guia de design que sai das escolhas, para copiar ou baixar. */
function GuidePanel({ value, appName }: { value: StudioChoice; appName: string }) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const text = designGuideOf(value, t, i18n.language, appName);
  return (
    <Panel title={t('style.guide.title')}>
      <p className="text-sm text-muted">{t('style.guide.hint')}</p>
      <pre className="max-h-64 overflow-auto rounded-md border border-rule bg-paper p-3 font-mono text-xs whitespace-pre-wrap">
        {text}
      </pre>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={() =>
            void navigator.clipboard?.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            })
          }
        >
          <Copy size={16} aria-hidden />
          {copied ? t('style.guide.copied') : t('style.guide.copy')}
        </Button>
      </div>
    </Panel>
  );
}
